import React, { useEffect, useMemo, useRef, useState } from "react";
import makeCloudinaryUrlForPhoto from "../../react/makeCloudinaryUrlForPhoto";
import "./ProductSlideshow.scss";

const cn = (...xs) => xs.filter(Boolean).join(" ");

// Fisher–Yates shuffle (non-mutating)
function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * ProductSlideshow
 * Props:
 * - categories: Array of categories with products
 * - products: (optional) direct products array; if provided, categories is ignored
 * - promoSlide: (optional) React node rendered as the first slide (index 0)
 * - className: optional
 * - ratio: CSS aspect-ratio string (default "16 / 9"), e.g. "1 / 1" for square
 * - imgWidth: number for Cloudinary transform (default 1200)
 */
export default function ProductSlideshow({
  categories = [],
  products,
  promoSlide,
  className,
  ratio = "16 / 9",
  imgWidth = 1700,
}) {
  const hasPromo = !!promoSlide;

  // Build the product list (with photos) and randomize
  const items = useMemo(() => {
    const fromCats = Array.isArray(categories)
      ? categories.flatMap((c) =>
          Array.isArray(c.products)
            ? c.products.map((p) => ({
                ...p,
                __category: c,
              }))
            : []
        )
      : [];

    const base = Array.isArray(products) ? products : fromCats;

    const withPhotos = base.filter((p) => !!p?.photo);
    return shuffle(withPhotos);
  }, [categories, products]);

  const [idx, setIdx] = useState(0);
  const [displayedUrl, setDisplayedUrl] = useState(null);
  const [isLoadingNext, setIsLoadingNext] = useState(false);
  const requestIdRef = useRef(0);
  const touchStartX = useRef(null);

  const productCount = items.length;
  const totalCount = productCount + (hasPromo ? 1 : 0);
  const isOnPromo = hasPromo && idx === 0;
  const productIdx = hasPromo ? idx - 1 : idx;

  const buildUrl = (photo) => makeCloudinaryUrlForPhoto(photo, { width: imgWidth });

  // Initialize first product image (preload for when user swipes past promo)
  useEffect(() => {
    if (!productCount) {
      setDisplayedUrl(null);
      return;
    }
    if (hasPromo) return; // promo is first slide; skip image preload for idx 0
    const first = items[0];
    const url = buildUrl(first.photo);
    const img = new Image();
    img.src = url;
    img.onload = () => setDisplayedUrl(url);
    img.onerror = () => setDisplayedUrl(null);
  }, [productCount, items, hasPromo]);

  // Preload when idx changes; skip when on promo slide
  useEffect(() => {
    if (isOnPromo || !productCount) return;

    const current = items[productIdx];
    if (!current) return;
    const rid = ++requestIdRef.current;

    if (current?.photo) {
      setIsLoadingNext(true);
      const url = buildUrl(current.photo);
      const img = new Image();
      img.src = url;
      img.onload = () => {
        if (requestIdRef.current === rid) {
          setDisplayedUrl(url);
          setIsLoadingNext(false);
        }
      };
      img.onerror = () => {
        if (requestIdRef.current === rid) {
          setIsLoadingNext(false);
        }
      };
    } else {
      setIsLoadingNext(false);
      setDisplayedUrl(null);
    }
  }, [idx, items, productCount, isOnPromo, productIdx]);

  const go = (dir) => {
    if (!totalCount) return;
    setIdx((i) => (i + dir + totalCount) % totalCount);
  };

  // Swipe gestures
  const onTouchStart = (e) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? null;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const endX = e.changedTouches?.[0]?.clientX ?? touchStartX.current;
    const delta = endX - touchStartX.current;
    const threshold = 40;
    if (Math.abs(delta) > threshold) {
      if (delta < 0) go(+1);
      else go(-1);
    }
    touchStartX.current = null;
  };

  const currentProduct = isOnPromo ? null : items[productIdx];
  const productName =
    currentProduct?.name?.en || currentProduct?.name || "";
  const categoryTitle =
    currentProduct?.__category?.name?.en || currentProduct?.__category?.name || "";

  return (
    <div className={cn("product-slideshow", className)}>
      <div
        className={cn("product-slideshow__media", isOnPromo && "is-promo-active")}
        style={{ "--ratio": ratio }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {isOnPromo ? (
          <div className="product-slideshow__promo-wrapper">
            {promoSlide}
          </div>
        ) : displayedUrl ? (
          <img
            key={displayedUrl}
            src={displayedUrl}
            alt={productName || "Product image"}
            loading="lazy"
            className={cn("product-slideshow__img", isLoadingNext && "is-loading-next")}
          />
        ) : (
          <div className="product-slideshow__fallback">
            <span>No image</span>
          </div>
        )}

        {/* Spinner while next image preloads */}
        {!isOnPromo && isLoadingNext && (
          <div className="product-slideshow__spinner" aria-hidden>
            <div className="spinner-dot" />
          </div>
        )}

        {/* Overlay (hidden on promo slide) */}
        {!isOnPromo && <div className="product-slideshow__overlay" />}

        {/* Text (hidden on promo slide) */}
        {!isOnPromo && (
          <div className="product-slideshow__text">
            {productName ? <h3 className="product-slideshow__title">{productName}</h3> : null}
            {categoryTitle ? <p className="product-slideshow__subtitle">{categoryTitle}</p> : null}
          </div>
        )}

        {/* Nav controls */}
        {totalCount > 1 && (
          <>
            <button
              type="button"
              className="product-slideshow__nav product-slideshow__nav--prev"
              aria-label="Previous"
              onClick={() => go(-1)}
            >
              <span aria-hidden>&#8249;</span>
            </button>
            <button
              type="button"
              className="product-slideshow__nav product-slideshow__nav--next"
              aria-label="Next"
              onClick={() => go(+1)}
            >
              <span aria-hidden>&#8250;</span>
            </button>
          </>
        )}
      </div>

      {/* Dots */}
      {totalCount > 1 && (
        <div className="product-slideshow__dots" role="tablist" aria-label="Slides pagination">
          {Array.from({ length: totalCount }, (_, i) => (
            <button
              key={i}
              type="button"
              className={cn("product-slideshow__dot", i === idx && "is-active")}
              onClick={() => setIdx(i)}
              role="tab"
              aria-selected={i === idx}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}